import { css } from '@emotion/react';
import parseHTML from 'html-react-parser';
import Image from 'next/legacy/image';
import React from 'react';

import type { V1AddOn } from '@/api/clients/ohip';
import { Box, Flex } from '@/box';
import { Trans } from '@/i18n';
import { Checkbox, Rocker } from '@/ui/form';
import { Card, Divider } from '@/ui/layout';
import { Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodySmall, Caption, Heading } from '@/ui/typography';

type AddOnCardProps = {
  isAdded: boolean;
  children: React.ReactNode;
  localisedAddOnPrice: string;
  /**
   * Used for aria-describedby property.
   */
  descriptionId?: string;
} & V1AddOn;

export const AddOnCard = (props: AddOnCardProps) => {
  const {
    colors,
    keylines,
    componentProperties: { addOnCard: addOnCardStyles },
  } = useTheme();

  const borderStyle = props.isAdded
    ? addOnCardStyles?.added?.border ?? `1px solid #3E3A37`
    : addOnCardStyles?.border ??
      keylines?.border ??
      `1px solid ${colors.primary300}`;

  if (!props.image?.url || !props.image.altText) {
    return null;
  }

  return (
    <Card
      p="m"
      border={borderStyle}
      testId="addon-card"
      css={css({
        ['label span']: addOnCardStyles?.ctaLabel,
        backgroundColor: addOnCardStyles?.backgroundColor,
      })}
    >
      {/* Stack vertically on Mobile & Tablet view */}
      <Flex flexDirection={['column', 'column', 'column', 'row']} gap={24}>
        <Box flex={2 / 5}>
          <Image
            src={props.image.url}
            alt={props.image.altText}
            width={657}
            height={405}
          />
        </Box>
        <Box flex={3 / 5}>
          <Heading as="h6" style="heading6" css={addOnCardStyles?.title}>
            {props.name}
          </Heading>
          <Spacer s="xxs" />
          <Caption css={addOnCardStyles?.subTitle}>
            <Trans
              defaults={props.priceContent}
              values={{ price: props.localisedAddOnPrice }}
            />
          </Caption>
          <Spacer s="xxs" />
          <BodySmall
            id={props.descriptionId}
            color="secondary300"
            css={addOnCardStyles?.description}
          >
            {parseHTML(props.description)}
          </BodySmall>
          <Spacer s="m" />
          <Divider css={addOnCardStyles?.divider} />
          <Spacer s="m" />
          {props.children}
        </Box>
      </Flex>
    </Card>
  );
};

AddOnCard.Checkbox = Checkbox;
AddOnCard.Rocker = Rocker;
